import React, { useState, useMemo } from 'react';
import { PencilIcon, TrashIcon, ChartBarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import UserForm from './UserForm';

const UsersTable = ({ users = [], onEdit, onDelete, onViewActivity, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchLower = searchTerm.toLowerCase();

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      return (
        user.email?.toLowerCase().includes(searchLower) ||
        user.companyName?.toLowerCase().includes(searchLower)
      );
    });
  }, [users, searchTerm]);

  if (!Array.isArray(users)) {
    console.error('Users prop is not an array:', users);
    return (
      <div className="bg-yellow-100 p-4 rounded-lg text-yellow-700">
        Error loading users data. Please try again.
      </div>
    );
  }

  const handleActivityClick = (user) => {
    console.log('Edit button clicked for user:', user); // Debugging log
    if (!user || !user._id) {
      console.error('Invalid user data:', user);
      return;
    }
    onViewActivity && onViewActivity(user);
  };

  const handleEditClick = (user) => {
    if (!user || !user._id) {
      console.error('Invalid user data for editing:', user);
      return;
    }
    onEdit && onEdit(user);
  };

  const handleDeleteClick = (userId) => {
    if (!userId) {
      console.error('Invalid user ID for deletion:', userId);
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      onDelete && onDelete(userId);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-orange-100">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Users</h2>
          <UserForm onSuccess={onRefresh} />
        </div>

        <div className="mt-4 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
            placeholder="Search users by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="p-6 text-center text-gray-500">
          {searchTerm
            ? 'No users found matching your search criteria.'
            : 'No users found. Add a new user to get started.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-orange-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-orange-100">
              {filteredUsers.map((user) => (
                <tr key={user._id} className="hover:bg-orange-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
                        {user.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.companyName}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.status || 'inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleActivityClick(user)}
                        className="text-orange-600 hover:text-orange-900 flex items-center space-x-1 group"
                      >
                        <ChartBarIcon className="h-5 w-5" />
                        <span className="group-hover:text-orange-900">View Activity</span>
                      </button>
                      <button
                        onClick={() => handleEditClick(user)}
                        className="text-orange-600 hover:text-orange-900 flex items-center space-x-1 group"
                      >
                        <PencilIcon className="h-5 w-5" />
                        <span className="group-hover:text-orange-900">Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(user._id)}
                        className="text-red-600 hover:text-red-900 flex items-center space-x-1 group"
                      >
                        <TrashIcon className="h-5 w-5" />
                        <span className="group-hover:text-red-900">Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersTable;